import TenantFormComponent from '../components/tenant-form.component';
import { TenantUpdate } from '../types/tenant.types';
import { useTenantGet, useTenantUpdate } from '../hooks/use-tenant.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useEffect, useState, useCallback } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DefaultValues } from 'react-hook-form';
import dayjs from 'dayjs';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

interface FormSubmitHandler {
  (data: TenantUpdate, resetForm: () => void): void;
}

const TenantUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [formDefaultValues, setFormDefaultValues] = useState<DefaultValues<TenantUpdate>>({});

  // Initialize all hooks before any conditional returns
  const { data, isLoading, error } = useTenantGet(id || ''); // Provide empty string as fallback
  const { mutateAsync: updateTenant, isPending } = useTenantUpdate();

  const extractFormDefaultValues = useCallback(async () => {
    if (!data?.data) return;
    const { data: tenantData } = data;

    setFormDefaultValues({
      name: tenantData.name ?? undefined,
      addressLine1: tenantData.addressLine1 ?? undefined,
      addressLine2: tenantData.addressLine2 ?? undefined,
      city: tenantData.city ?? undefined,
      state: tenantData.state ?? undefined,
      zipCode: tenantData.zipCode ?? undefined,
      primaryEmail: tenantData.primaryEmail ?? undefined,
      secondaryEmail: tenantData.secondaryEmail ?? undefined,
      primaryPhone: tenantData.primaryPhone ?? undefined,
      secondaryPhone: tenantData.secondaryPhone ?? undefined,
      defaultLanguage: tenantData.defaultLanguage ?? undefined,
      defaultCurrency: tenantData.defaultCurrency ?? undefined,
      brandingColors: tenantData.brandingColors ?? undefined,
      logo: tenantData.logo ? [tenantData.logo as string] : undefined,
    });
  }, [data]);

  useEffect(() => {
    let isMounted = true; // to prevent memory leaks
    extractFormDefaultValues();
    return () => {
      isMounted = false;
    };
  }, [extractFormDefaultValues]);

  const onFormSubmit = useCallback(
    async (data: TenantUpdate, resetForm: () => void) => {
      if (!id) return; // Early return if no id

      // Reset validation errors on new submission
      setValidationErrors([]);
      try {
        await updateTenant({ id, data });
        message.success(t('crud.update.success', { entity: t('tenants.tenant') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('tenants.tenant') })
        );
        message.error(errorMessage);
      }
    },
    [id, updateTenant, t, navigate]
  );

  // Handle the case where no ID is provided
  if (!id) {
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <HeaderRow
          title={t('crud.update.title', { entity: t('tenants.tenant') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('crud.details.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('tenants.tenant') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('tenants.tenant') })}
        showBackButton={true}
      />
      <div className="row mt-3">
        <TenantFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          columns={3}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(TenantUpdateContainer, 'tenant.update');
